// src/components/ConfirmModal.tsx
import React, { useEffect } from 'react';
import { AlertTriangle, X, Info, HelpCircle, Check } from 'lucide-react';

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'danger' | 'warning' | 'info';
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Подтвердить',
  cancelText = 'Отмена',
  onConfirm,
  onCancel,
  variant = 'danger',
}) => {
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'auto';
    return () => { document.body.style.overflow = 'auto'; };
  }, [isOpen]);

  // Escape закрывает
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') onConfirm();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onCancel, onConfirm]);

  if (!isOpen) return null;

  const meta = {
    danger: {
      gradient: 'linear-gradient(135deg, #ef4444, #dc2626)',
      iconColor: '#ef4444',
      iconBg: 'rgba(239,68,68,0.12)',
      Icon: AlertTriangle,
    },
    warning: {
      gradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
      iconColor: '#f59e0b',
      iconBg: 'rgba(245,158,11,0.12)',
      Icon: AlertTriangle,
    },
    info: {
      gradient: 'linear-gradient(135deg, #6a11cb, #2575fc)',
      iconColor: '#2575fc',
      iconBg: 'rgba(37,117,252,0.12)',
      Icon: Info,
    },
  }[variant];

  const Icon = meta.Icon;

  return (
    <div style={styles.overlay} onClick={onCancel}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button style={styles.closeButton} onClick={onCancel} aria-label="Закрыть">
          <X size={18} />
        </button>

        <div style={styles.header}>
          <div style={{ ...styles.iconCircle, background: meta.iconBg }}>
            <Icon size={26} color={meta.iconColor} />
          </div>
          <h3 style={styles.title}>{title}</h3>
        </div>

        <div style={styles.body}>
          <p style={styles.message}>{message}</p>
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelButton} onClick={onCancel}>
            {cancelText}
          </button>
          <button
            style={{ ...styles.confirmButton, background: meta.gradient }}
            onClick={onConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(15, 23, 42, 0.55)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100000,
    padding: 20,
    animation: 'confirmFadeIn 0.15s ease-out',
  },
  modal: {
    zIndex: 100000,
    background: '#fff',
    borderRadius: 20,
    padding: '28px 24px 24px',
    width: '100%',
    maxWidth: 440,
    boxShadow: '0 25px 60px rgba(15,23,42,0.3), 0 4px 12px rgba(0,0,0,0.08)',
    position: 'relative',
    animation: 'confirmScaleIn 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
    fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
  },
  closeButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#94a3b8',
    padding: 6,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  iconCircle: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  title: {
    fontSize: 18,
    fontWeight: 800,
    color: '#1a1a2e',
    margin: 0,
    textAlign: 'center',
    lineHeight: 1.3,
  },
  body: {
    marginBottom: 24,
    padding: '0 4px',
  },
  message: {
    fontSize: 14,
    color: '#475569',
    margin: 0,
    lineHeight: 1.55,
    textAlign: 'center',
  },
  footer: {
    display: 'flex',
    gap: 10,
    justifyContent: 'stretch',
  },
  cancelButton: {
    flex: 1,
    background: '#f1f5f9',
    color: '#475569',
    border: 'none',
    borderRadius: 12,
    padding: '12px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
  },
  confirmButton: {
    flex: 1,
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    padding: '12px 20px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    boxShadow: '0 4px 12px rgba(239,68,68,0.3)',
    transition: 'all 0.15s',
  },
};

// Глобальные keyframes
if (typeof document !== 'undefined' && !document.getElementById('confirm-modal-keyframes')) {
  const style = document.createElement('style');
  style.id = 'confirm-modal-keyframes';
  style.textContent = `
    @keyframes confirmFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes confirmScaleIn {
      from { opacity: 0; transform: scale(0.92); }
      to { opacity: 1; transform: scale(1); }
    }
  `;
  document.head.appendChild(style);
}

export default ConfirmModal;
